import { JSX } from 'react';
import {
  AtMedia,
  AtHeader,
  IAtClickableProps,
  AtClickable,
  AtDivider,
  AtIcon,
} from '@/components/atoms';
import { EThemes, TSpacing } from '@/lib/contentstack-sdk/types';
import { THeaderSupportedElements } from '@/components/atoms/at-header/at-header.types';
import { IAtMediaProps } from '@/components/atoms/at-media/at-media.types';
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';
import orMiniCtaBlockDVariants from './or-mini-cta-block-d.variants';
import classNames from 'classnames';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import { EIcons } from '@/components/atoms/at-icon/icons';

export interface IOrMiniCtaBlockDProps {
  top_spacing: TSpacing;
  bottom_spacing: TSpacing;
  body_copy: string;
  theme: keyof typeof EThemes;
  heading_element: THeaderSupportedElements;
  heading_variant: THeaderSupportedElements;
  heading: string;
  image: IAtMediaProps;
  top_cta?: IAtClickableProps;
  bottom_cta?: IAtClickableProps;
  top_divider?: boolean;
  bottom_divider?: boolean;
}

export const OrMiniCtaBlockD = ({
  theme,
  image,
  top_cta,
  bottom_cta,
  body_copy,
  heading_element,
  heading_variant,
  heading,
  top_spacing,
  bottom_spacing,
  top_divider = false,
  bottom_divider = false,
}: IOrMiniCtaBlockDProps): JSX.Element => {
  const cmsSpacing = orMiniCtaBlockDVariants({
    topSpacing: top_spacing,
    bottomSpacing: bottom_spacing,
  });

  return (
    <div className="container" data-theme={EThemes[theme]}>
      {top_divider && (
        <AtDivider
          decorative
          color={EDividerColors.GREY}
          direction={EDividerDirectionalVariants.HORIZONTAL}
          intent={EDividerVariants.THIN}
          customClasses="container"
        />
      )}
      <div className={classNames(cmsSpacing)}>
        <div className="flex flex-col items-center justify-between rounded-30 bg-skin-secondary px-[0.969rem] py-13 lg:grid lg:grid-cols-3 lg:gap-16 lg:px-23 lg:py-15">
          <div className="mb-8 max-h-[10.625rem] w-full overflow-hidden rounded-[18.75rem] lg:col-span-1 lg:mb-0 lg:max-h-[13.438rem]">
            <AtMedia
              {...image}
              src={image.src}
              className="!relative"
              sizes="(max-width: 1023px) 50vw, 33.3vw"
            />
          </div>
          <div className="lg:col-span-2 lg:flex lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-8 flex flex-col gap-6 text-center lg:mb-0 lg:text-left">
              <AtHeader element={heading_element} variant={heading_variant}>
                {heading}
              </AtHeader>
              <RenderRichText body={body_copy} />
            </div>

            <div className="ml-7 flex flex-col items-center gap-5 lg:ml-0 lg:items-end lg:gap-4">
              <AtClickable
                className="w-fit lg:w-full"
                iconPosition={top_cta?.iconPosition}
                interactLocation="or-mini-cta-block-d"
                {...top_cta}
              >
                {top_cta?.icon && (
                  <span className="text-lg lg:text-24">
                    <AtIcon
                      icon={top_cta?.icon as EIcons}
                      color="var(--color-neutral-500)"
                    />
                  </span>
                )}
              </AtClickable>
              <AtClickable
                className="w-fit"
                interactLocation="or-mini-cta-block-d"
                {...bottom_cta}
              >
                {bottom_cta?.icon && (
                  <span className="text-lg lg:text-24">
                    <AtIcon
                      icon={bottom_cta?.icon as EIcons}
                      color="var(--color-neutral-500)"
                    />
                  </span>
                )}
              </AtClickable>
            </div>
          </div>
        </div>
        {bottom_divider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </div>
    </div>
  );
};
